import { useState, useEffect } from "react";

import { useTranslation } from "react-i18next";

// MUI
import {
  Typography,
  Stack,
  Tab,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";

// Icons
import { LightMode, DarkMode, Check } from "@mui/icons-material";

// Styles
import { styles } from "./styles";
import { styles as tabStyles } from "Common/Components/MNTabs/styles";

//Libraries
import { DateTime } from "luxon";

// Components
import { getLocalizedDate, getLocalizedTime } from "Components/Language/Common";
import { MNButton, MNTabs } from "Common/Components";

const ViewMoreAppointmentsModal = (params) => {
  const { clinic, currentDate, createAppointment, closeModal, openModal } =
    params;
  const [availableSlots, setAvailableSlots] = useState({});
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTimeSlotId, setSelectedTimeSlotId] = useState("");
  const [selectedSlot, setSelectedSlot] = useState(null);
  const { t } = useTranslation();

  useEffect(() => {
    if (clinic.availableSlots && clinic.availableSlots.length > 0) {
      const processedSlots = clinic.availableSlots.reduce((acc, slot) => {
        const dateKey = slot.date;

        // Initialize AM and PM if not already present
        if (!acc[dateKey]) {
          acc[dateKey] = { AM: {}, PM: {} };
        }

        // Determine if it's AM or PM
        const startTimeObj = DateTime.fromFormat(slot.startTime, "h:mm a");
        const period = startTimeObj.hour < 12 ? "AM" : "PM";

        // Store the start and end times under the respective period
        acc[dateKey][period][`${dateKey}_${slot.startTime}`] = {
          startTime: slot.startTime,
          endTime: slot.endTime,
          uniqueSlots: slot.uniqueSlots,
        };

        return acc;
      }, {});
      setAvailableSlots(processedSlots);

      setSelectedDate(
        currentDate ? currentDate : Object.keys(processedSlots)[0]
      );
    }
  }, [clinic.availableSlots, currentDate]);

  const createAppointmentDetails = () => {
    if (!selectedSlot) return;
    createAppointment(clinic, selectedDate, selectedSlot);
    closeModal();
  };

  const DateTabs = () => {
    const dates = Object.keys(availableSlots);

    return (
      <MNTabs
        value={selectedDate ? dates.indexOf(selectedDate) : 0}
        onChange={(_, newValue) => setSelectedDate(dates[newValue])}
        tabName={t("viewMoreAppointmentsModal.dateTabs")}
      >
        {dates.map((date, index) => {
          return (
            <Tab
              key={index}
              label={getLocalizedDate(date, "cccc, MMM d", t)}
              sx={tabStyles.MNTabStyle(date === selectedDate)}
            />
          );
        })}
      </MNTabs>
    );
  };

  const TimeSlotList = ({ slots, time }) => {
    return (
      <Stack direction="column" spacing={1} sx={styles.timeStack}>
        <Stack direction="row" spacing={1} sx={styles.timePeriodStack}>
          {time === "am" ? (
            <LightMode sx={styles.timeIcon} />
          ) : (
            <DarkMode sx={styles.timeIcon} />
          )}
          <Typography variant="body2" color="primary" fontWeight="bold">
            {t(`viewMoreAppointmentsModal.${time}`)}
          </Typography>
        </Stack>
        {slots && Object.keys(slots).length ? (
          Object.entries(slots).map(([timeSlotId, slotDetails]) => (
            <Button
              key={timeSlotId}
              sx={styles.timeSlotButton(selectedTimeSlotId === timeSlotId)}
              variant={"outlined"}
              onClick={() => {
                setSelectedTimeSlotId(timeSlotId);
                setSelectedSlot(slotDetails);
              }}
            >
              {getLocalizedTime(slotDetails.startTime)}
            </Button>
          ))
        ) : (
          <Typography variant="subtitle1" textAlign="center">
            {t("viewMoreAppointmentsModal.noAppointmentsAvailable")}
          </Typography>
        )}
      </Stack>
    );
  };

  return (
    <Dialog
      open={openModal}
      onClose={closeModal}
      slotProps={{ paper: { sx: styles.dialog } }}
    >
      <DialogTitle sx={styles.dialogTitle}>
        <Typography sx={styles.clinicName}>
          {t("clinic.name", { clinic: clinic.Clinic }) || ""}
        </Typography>
        <Typography sx={styles.clinicSubtitle}>
          {t("viewMoreAppointmentsModal.availableAppointments")}
        </Typography>
        <DateTabs />
      </DialogTitle>
      <DialogContent sx={styles.dialogContent}>
        <Stack direction="row" sx={styles.timeSlotsContainer}>
          <TimeSlotList slots={availableSlots[selectedDate]?.AM} time="am" />
          <TimeSlotList slots={availableSlots[selectedDate]?.PM} time="pm" />
        </Stack>
      </DialogContent>

      <DialogActions sx={styles.actionButtonsContainer}>
        <MNButton variant="text" onClick={closeModal}>
          {t("buttons.cancel")}
        </MNButton>
        <MNButton
          disabled={!selectedSlot}
          onClick={createAppointmentDetails}
          variant="contained"
          startIcon={<Check />}
        >
          {t("viewMoreAppointmentsModal.book")}
        </MNButton>
      </DialogActions>
    </Dialog>
  );
};

export default ViewMoreAppointmentsModal;
